import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaStar, FaCalendarAlt, FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const SimpleNewReleases = () => {
    const [newReleases, setNewReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const { isDark } = useTheme();

    useEffect(() => {
        const fetchNewReleases = async () => {
            try {
                setLoading(true);
                const currentYear = new Date().getFullYear();
                const res = await api.get('/movies/search', {
                    params: {
                        sort_by: 'release_date.desc',
                        from_year: currentYear - 1
                    }
                });
                setNewReleases(res.data.slice(0, 20)); // Take top 20
            } catch (err) {
                console.error('Failed to fetch new releases:', err);
                setError('Failed to load new releases');
            } finally {
                setLoading(false);
            }
        };

        fetchNewReleases();
    }, []);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % Math.max(1, newReleases.length - 3));
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + Math.max(1, newReleases.length - 3)) % Math.max(1, newReleases.length - 3));
    };

    const MovieCard = ({ movie }) => (
        <div className="group relative theme-bg-secondary rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex-shrink-0">
            <Link to={`/movie/${movie.id}`} className="block">
                <div className="relative overflow-hidden">
                    {movie.poster_path ? (
                        <img
                            src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                            alt={movie.title}
                            className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                    ) : (
                        <div className="w-full h-80 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                            <div className="text-center text-gray-500 dark:text-gray-400">
                                <FaPlay className="text-4xl mx-auto mb-2" />
                                <span className="text-sm">No Image Available</span>
                            </div>
                        </div>
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <FaPlay className="text-white text-3xl bg-white/20 rounded-full p-3 backdrop-blur-sm" />
                        </div>
                    </div>

                    {/* New Badge */}
                    <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                        <span>🆕</span>
                        <span>New</span>
                    </div>

                    {/* Rating Badge */}
                    {movie.vote_average > 0 && (
                        <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                            <FaStar className="text-xs" />
                            <span>{movie.vote_average.toFixed(1)}</span>
                        </div>
                    )}
                </div>

                <div className="p-4">
                    <h3 className="font-bold theme-text-primary text-lg mb-2 line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                        {movie.title}
                    </h3>

                    {movie.release_date && (
                        <div className="flex items-center theme-text-tertiary text-sm mb-2">
                            <FaCalendarAlt className="mr-2" />
                            <span>{new Date(movie.release_date).getFullYear()}</span>
                        </div>
                    )}

                    {movie.overview && (
                        <p className="theme-text-secondary text-sm line-clamp-3">
                            {movie.overview}
                        </p>
                    )}
                </div>
            </Link>
        </div>
    );

    if (loading) {
        return (
            <div className="py-12">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <FaSpinner className="text-4xl animate-spin mx-auto mb-4 text-purple-600" />
                        <p className="theme-text-secondary">Loading new releases...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-12">
                <div className="text-center">
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            </div>
        );
    }

    const visibleMovies = newReleases.slice(currentIndex, currentIndex + 4);

    return (
        <div className="py-6 sm:py-8 lg:py-12">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8">
                    <div className="mb-4 sm:mb-0">
                        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold theme-text-primary mb-2">
                            🆕 New Releases
                        </h2>
                        <p className="theme-text-secondary text-sm sm:text-base">
                            Discover the latest movies hitting theaters and streaming platforms
                        </p>
                    </div>

                    <Link
                        to="/?filter=new"
                        className="hidden sm:flex items-center space-x-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
                    >
                        <span>View All</span>
                        <FaChevronRight className="text-sm" />
                    </Link>
                </div>

                {/* Movies Carousel */}
                <div className="relative">
                    <div className="flex space-x-3 sm:space-x-4 lg:space-x-6 overflow-hidden">
                        {visibleMovies.map((movie) => (
                            <div key={movie.id} className="w-64 sm:w-72 lg:w-80 flex-shrink-0">
                                <MovieCard movie={movie} />
                            </div>
                        ))}
                    </div>

                    {/* Navigation Buttons */}
                    {newReleases.length > 4 && (
                        <>
                            <button
                                onClick={prevSlide}
                                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-200 -ml-2 sm:-ml-4"
                            >
                                <FaChevronLeft className="text-sm sm:text-lg" />
                            </button>

                            <button
                                onClick={nextSlide}
                                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-200 -mr-2 sm:-mr-4"
                            >
                                <FaChevronRight className="text-sm sm:text-lg" />
                            </button>
                        </>
                    )}
                </div>

                {/* Mobile View All Button */}
                <div className="sm:hidden mt-6 text-center">
                    <Link
                        to="/?filter=new"
                        className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                        <span>View All New Releases</span>
                        <FaChevronRight className="text-sm" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SimpleNewReleases;
